/**
 * @author       yontaek@zumper.com (Yon Taek Chung)
 * @copyright    Copyright (c) 2020, Zumper
 * @description  useGetTourAvailability hook
 */

import { useBoolean } from '@chakra-ui/react'
import { selectNow } from '@zcode-ui/web-zumper-global-state'
import type { SerializableDate } from '@zcode/bizos'
import type { IntegratedTourType } from '@zcode/enums'
import type { Property } from '@zcode/state-type'
import type { MaybeEmpty } from '@zcode/utility-types'
import { isSameDay } from 'date-fns'
import { useEffect, useMemo } from 'react'
import { useSelector } from 'react-redux'
import { useIdle, useInterval } from 'react-use'
import { logger } from '../../../../../logger'
import {
  selectAvailableTourTimes,
  selectFetchingTourAvailability,
  selectSelectedTourType,
  selectTourAvailabilityFetchedAt,
  selectTourIntegrationData,
  selectTourIntegrationForGroupIdFetchedAt,
} from '../../../../../modules/multifamilyTourTimePicker/selectors'
import { fetchTourAvailability } from '../../../../../modules/multifamilyTourTimePicker/thunks/fetchTourAvailability'
import { useDispatch } from '../../../../../modules/useDispatch'
import { getKnockIntervalChoices } from '../helpers'

const DATE_SUBSTR_COUNT = 19
const FIVE_MINUTES = 300_000

const isMoreThanFiveMinutesOld = (fetchedAt: SerializableDate) =>
  Date.now() - fetchedAt.getTime() > FIVE_MINUTES
const isFromADayThatIsntToday = (fetchedAt: SerializableDate) =>
  !isSameDay(new Date(), fetchedAt.getTime())

export const useGetTourAvailability = (
  entity: MaybeEmpty<
    Pick<Property, 'groupId' | 'isTourDisabled' | 'mightHaveInstantTourScheduling' | 'tz'>
  >,
  _type: IntegratedTourType | undefined,
  {
    mayMakeInitialFetch = true,
    skip = false,
  }: { mayMakeInitialFetch?: boolean; skip?: boolean } = {}
) => {
  const dispatch = useDispatch()
  const now = useSelector(selectNow)
  const selectedTourType = useSelector(selectSelectedTourType)
  const type = _type || selectedTourType
  const availableTimes = useSelector((state) =>
    entity.groupId ? selectAvailableTourTimes(state, entity.groupId, type) : null
  )
  const fetchedAt = useSelector((state) =>
    entity.groupId ? selectTourAvailabilityFetchedAt(state, entity.groupId, type) : null
  )
  const fetching = useSelector((state) =>
    entity.groupId ? selectFetchingTourAvailability(state, entity.groupId, type) : null
  )
  const fetchedTourIntegrationAt = useSelector(
    (state) => !!entity.groupId && selectTourIntegrationForGroupIdFetchedAt(state, entity.groupId)
  )
  const integration = useSelector((state) =>
    entity.groupId ? selectTourIntegrationData(state, entity.groupId) : null
  )
  const [toggled, { toggle }] = useBoolean()

  const isIdle = useIdle()
  useInterval(toggle, FIVE_MINUTES)

  useEffect(() => {
    const fetchData = async () => {
      if (!skip && entity.groupId) {
        // because we use the useGetTourAvailability() hook in multiple places, the code flows
        // like this:
        // 1. we call useGetTourAvailability() in 4 components, all at "the same" time
        // 2. each of those 4 instances of this hook get fetching/existingBuildingId from state
        //    (lines 39-40 above) and all get the same value (e.g. false and null)
        // 3. each of those 4 instances run this useEffect, they all see "oh, we're not fetching?
        //    guess i should take care of fetching, then"
        // 4. we make the same (rather slow) API call 4 times :-(
        //
        // To fix that situation, we re-get stuff from state here, just before we make the API call
        const state = dispatch((_, getState) => getState())
        const fetchedTourIntegrationAt = selectTourIntegrationForGroupIdFetchedAt(
          state,
          entity.groupId
        )
        const fetchedAt = selectTourAvailabilityFetchedAt(state, entity.groupId, type)
        const fetching = selectFetchingTourAvailability(state, entity.groupId, type)
        if (
          // fetch tour availability immediately if we haven't fetched it for this building yet
          (!fetchedTourIntegrationAt ||
            // otherwise, refresh it if we're not currently fetching it, the user is active, and it's been 5 minutes
            (!fetching &&
              !isIdle &&
              (!fetchedAt ||
                isMoreThanFiveMinutesOld(fetchedAt) ||
                isFromADayThatIsntToday(fetchedAt)))) &&
          (mayMakeInitialFetch || fetchedAt)
        ) {
          try {
            await dispatch(fetchTourAvailability({ entity, type }))
          } catch (err) {
            logger.warn('failed to load tour availability: ', err)
          }
        }
      }
    }

    void fetchData()
  }, [dispatch, entity, fetchedAt, fetching, isIdle, mayMakeInitialFetch, skip, toggled, type])

  return useMemo(() => {
    if (entity.groupId && fetchedTourIntegrationAt && Array.isArray(availableTimes)) {
      const dateArr = availableTimes.map((date) => new Date(date))
      const providedTimesInterval = getKnockIntervalChoices(dateArr, now, entity.tz)
      return { fetching: false, providedTimesInterval }
    }
    return {
      fetching:
        !!entity.groupId &&
        !skip &&
        mayMakeInitialFetch &&
        (!fetchedTourIntegrationAt || !!integration?.tourTypes.includes(type)) &&
        !fetchedAt,
      providedTimesInterval: undefined,
    }
  }, [
    availableTimes,
    entity,
    fetchedAt,
    fetchedTourIntegrationAt,
    integration,
    mayMakeInitialFetch,
    now,
    skip,
    type,
  ])
}
